import { queryKeys } from '@query/keys'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import React, { useContext, useEffect, useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { useAccountReadContext } from '@context/AccountContextContext'
import { useModal } from '@context/ModalsContext'
import { useSnackbar } from '@context/SnackbarContext'

import { Link } from '@ui/components/Link'
import { PageSection } from '@ui/components/PageSection'
import { CirclePlusIcon } from '@ui/icons/CirclePlus.icon'
import { InfoIcon } from '@ui/icons/Info.icon'
import { TableSummary } from '@ui/v2/TableSummary/TableSummary'

import { useApiClient } from '@utils/ApiClient'
import { SharedContext } from '@utils/SharedContext'
import { ClientApiClient } from '@utils/clientApi'
import { useWindowResize } from '@utils/domUtils'
import { isZero } from '@utils/validations'
import { WalletRestrictions, isRestricted } from '@utils/wallet.utils'

import { WalletCurrency, WalletDto, WalletStateEnum, WalletTypeEnum } from '@model/WalletDto'

import { useFormatNumber } from '@hooks/useFormatNumber'
import { ScrollToIds, useScrollToElementIds } from '@hooks/useScrollToElementIds'
import { useScrollToTop } from '@hooks/useScrollToTop'
import { useWithdrawalPageNavigation } from '@hooks/useWithdrawalPageNavigation'

import { Loading } from '@shared/components/Loading/Loading'
import { InformationModal } from '@shared/modals/Information.modal'
import { SupportModal } from '@shared/modals/Support.modal'

import { WalletBalancesCard } from './WalletBalancesCard'
import { WalletBalancesTable } from './WalletBalancesTable'
import { SelectNewWalletModal } from './modals/SelectNewWallet.modal'

interface WalletBalancesProps {
  walletType: WalletTypeEnum
}

export const WalletBalances: React.FC<WalletBalancesProps> = (props) => {
  useScrollToTop()
  const { walletType } = props
  const { t } = useTranslation()
  const location = useLocation()
  const isMobile = useWindowResize()
  const apiClient = useApiClient(ClientApiClient)
  const { account } = useAccountReadContext()
  const [sharedState, setSharedState] = useContext(SharedContext)
  const { formatMoney } = useFormatNumber()

  const restrictionsModal = useModal(SupportModal)
  const { handleWithdrawalNavigation, walletWithdrawLoadingId } = useWithdrawalPageNavigation()
  const balanceModal = useModal(InformationModal, {
    title: t('Wallet.Wallets Balance'),
    description: () => (
      <Trans
        i18nKey='Wallet.Your current balance is the total of your {{ value }} balance(s)'
        components={{
          1: (
            <Link
              title={t('Currency you selected')}
              size={'lg'}
              to={`/profile/account-settings`}
              onClick={() => balanceModal.close()}
            />
          ),
        }}
        values={{ value: 'wallet' }}
      />
    ),
  })

  const queryClient = useQueryClient()

  const { data = [], isLoading } = useQuery({
    queryKey: queryKeys.transactionPageData({
      walletType,
      accountCurrency: account?.displayCurrency?.id,
    }),
    queryFn: async () => {
      const result = await Promise.all([
        account?.displayCurrency?.id &&
          apiClient.getTotalBalanceByCurrency(account.displayCurrency.id, WalletTypeEnum.ETD),
        apiClient.getAvailableWallets(walletType),
        apiClient.getWallets({ walletTypeId: walletType }),
        !sharedState.walletTypes.length && apiClient.getWalletTypes(),
      ])

      const [, , , walletTypes = []] = result
      setSharedState((currentState) => ({
        ...currentState,
        walletTypes: walletTypes || [],
      }))

      return result
    },
    refetchOnMount: 'always',
  })
  const [totalBalance, availableWalletTypes = [], wallets = []] = data

  const forceRefresh = () => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.transactionPageData({
        walletType,
        accountCurrency: account?.displayCurrency?.id,
      }),
    })
  }

  const isWalletCreationRestricted =
    isRestricted(WalletRestrictions.CREATE_WALLET, account?.restrictions) ||
    isZero(availableWalletTypes.length)

  const { addSnackbar } = useSnackbar()

  const isWithdrawalRestricted = (wallet: WalletDto | undefined) =>
    isRestricted(WalletRestrictions.WITHDRAW, wallet?.restrictions)

  const handleClickWithdrawal = (walletId: string) => {
    const wallet = wallets.find((wallet) => wallet.id === walletId)
    if (isWithdrawalRestricted(wallet)) {
      addSnackbar.error({ message: t('This action is not available') })
      return
    }
    if (wallet && wallet.balance < 0) {
      addSnackbar.error({ message: t('Withdrawals for this wallet are unavailable.') })
      return
    }
    handleWithdrawalNavigation({ walletId })
  }

  const clearNewWalletCreationState = () => {
    forceRefresh()
  }

  const addWalletModal = useModal(SelectNewWalletModal)

  const handleNewWallet = async (data: WalletCurrency) => {
    if (data) {
      try {
        await apiClient.addWallet({
          walletTypeId: walletType,
          walletStateId: WalletStateEnum.Active,
          currencyId: data.id,
        })
      } finally {
        clearNewWalletCreationState()
      }
    }
    clearNewWalletCreationState()
  }

  const getWalletCreationRestrictionInfo = useMemo(() => {
    if (isRestricted(WalletRestrictions.CREATE_WALLET, account?.restrictions)) {
      return {
        title: 'Restricted Access',
        description: 'errors.Client is restricted to create a wallet',
      }
    }
    if (isZero(availableWalletTypes.length)) {
      return {
        title: 'Wallet.Maximum limit of Wallets reached!',
        description:
          'Wallet.You have reached the maximum limit of Wallets! Please contact our Support team for more information.',
      }
    }
    return {
      title: 'Restricted Access',
      description: 'errors.Client is restricted to create a wallet',
    }
  }, [account, availableWalletTypes])

  const { scrollIntoView } = useScrollToElementIds()

  useEffect(() => {
    if (location.state?.walletId || location.state?.wallets) {
      scrollIntoView([ScrollToIds.BalancesWalletsHeader])
    }
  }, [location, scrollIntoView])

  return (
    <div>
      <PageSection
        id={ScrollToIds.BalancesWalletsHeader}
        title={isMobile ? '' : t('Wallet.Wallets')}
        renderActions={[
          {
            disabled: isWalletCreationRestricted,
            onClick: () =>
              addWalletModal.open({ walletTypes: availableWalletTypes, onSelect: handleNewWallet }),
            title: `${t('Wallet.Add Wallet')}`,
            icon: CirclePlusIcon,
          },
          ...(isWalletCreationRestricted
            ? [
                {
                  onClick: () =>
                    restrictionsModal.open({
                      title: t(getWalletCreationRestrictionInfo.title),
                      description: () => (
                        <span
                          dangerouslySetInnerHTML={{
                            __html: t(getWalletCreationRestrictionInfo.description),
                          }}
                        />
                      ),
                    }),
                  icon: InfoIcon,
                },
              ]
            : []),
        ]}
      />
      <TableSummary
        isLoading={isLoading}
        summaryItems={[
          {
            title: t('Wallet.Wallets Balance'),
            infoClick: () => balanceModal.open(),
            value: <>≈{formatMoney(totalBalance?.balance, totalBalance?.currency.id)}</>,
          },
        ]}
      />
      <Loading showLoadingIcon isLoading={isLoading}>
        <div>
          {isMobile ? (
            <div>
              {wallets.map((wallet) => (
                <WalletBalancesCard
                  key={wallet.id}
                  onWithdrawal={handleClickWithdrawal}
                  wallet={wallet}
                />
              ))}
            </div>
          ) : (
            <WalletBalancesTable
              wallets={wallets}
              onWithdrawal={handleClickWithdrawal}
              isLoading={!!walletWithdrawLoadingId}
            />
          )}
        </div>
      </Loading>
    </div>
  )
}
